import { Wrench } from 'lucide-react'
import { useTheme } from '../../theme/hooks'

type MaintenancePageProps = {
  title?: string
  message?: string
}

const defaultTitle = 'Pause technique !'
const defaultMessage =
  'Maintenance en cours. Nous revenons très vite ! Merci de votre patience.'

export default function MaintenancePage({
  title = defaultTitle,
  message = defaultMessage,
}: MaintenancePageProps) {
  const { theme } = useTheme()

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden px-6 py-10"
      style={{ backgroundColor: theme.background.primary }}
    >
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute -left-32 -top-32 h-[520px] w-[520px] rounded-full blur-3xl"
          style={{ backgroundColor: `${theme.button.primary.bg}22` }}
        />
        <div
          className="absolute -bottom-40 -right-40 h-[560px] w-[560px] rounded-full blur-3xl"
          style={{ backgroundColor: `${theme.text.link}18` }}
        />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.7) 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        />
      </div>

      {/* Card */}
      <div
        className="relative w-full max-w-lg rounded-2xl border p-8 shadow-2xl backdrop-blur-md"
        style={{
          backgroundColor: `${theme.surface.elevated}E6`,
          borderColor: theme.border.primary,
          boxShadow: theme.modal.shadow,
        }}
      >
        <div className="flex items-center justify-between">
          <img src="/elda-logo.svg" alt="Elda" className="h-10 w-auto" />
        </div>

        <div className="mt-8 flex items-center gap-4">
          <div className="relative">
            <div
              className="relative rounded-2xl p-4"
              style={{
                backgroundColor: `${theme.button.primary.bg}12`,
                border: `1px solid ${theme.button.primary.border}`,
              }}
            >
              <Wrench
                className="h-7 w-7"
                style={{ color: theme.button.primary.bg }}
              />
            </div>
          </div>

          <div className="min-w-0">
            <h1
              className="truncate text-2xl font-semibold"
              style={{ color: theme.text.primary }}
            >
              {title}
            </h1>
            <p className="mt-1 text-sm" style={{ color: theme.text.secondary }}>
              {message}
            </p>
          </div>
        </div>

        <div className="mt-7">
          <div
            className="relative h-1.5 w-full overflow-hidden rounded-full"
            style={{ backgroundColor: theme.background.secondary }}
            aria-hidden="true"
          >
            <div
              className="absolute inset-y-0 left-0 w-[38%] animate-[maintenance-indeterminate_1.8s_ease-in-out_infinite]"
              style={{
                backgroundImage: `linear-gradient(90deg,
                  ${theme.button.primary.bg}00 0%,
                  ${theme.button.primary.bg}AA 40%,
                  ${theme.button.primary.bg}FF 50%,
                  ${theme.button.primary.bg}AA 60%,
                  ${theme.button.primary.bg}00 100%
                )`,
                filter: 'drop-shadow(0 0 10px rgba(59,130,246,0.35))',
                willChange: 'transform, opacity',
              }}
            />
          </div>
          <div
            className="mt-3 text-[11px]"
            style={{ color: theme.text.tertiary }}
          >
            Deploiement en cours — actualisation automatique.
          </div>
        </div>
      </div>

      <style>{`
        @keyframes maintenance-indeterminate {
          0% { transform: translateX(-140%); opacity: 0; }
          10% { opacity: 0.9; }
          50% { opacity: 1; }
          90% { opacity: 0.9; }
          100% { transform: translateX(320%); opacity: 0; }
        }
      `}</style>
    </div>
  )
}
